

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FaStore, FaTruck, FaUserFriends, FaBoxOpen, FaShoppingCart } from "react-icons/fa";
import { useLoader } from "../Component/Context/LoaderProvider.jsx";

const Counter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 20);

    const interval = setInterval(() => {
      start += increment;
      if (start >= end) {
        clearInterval(interval);
        setCount(end);
      } else {
        setCount(Math.ceil(start));
      }
    }, 20);

    return () => clearInterval(interval);
  }, [end, duration]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

const Cloudbannerall = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { showLoader, hideLoader } = useLoader();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [noBanner, setNoBanner] = useState(false);

  // Default banner image (fallback)
  const DEFAULT_BANNER = "https://via.placeholder.com/1920x700?text=Welcome+to+Maamaas+House";
  const DEFAULT_LOGO = "https://via.placeholder.com/100x100?text=Logo";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toDataUrl = useCallback((img, mime = "jpeg") => {
    if (!img) return "";
    if (img.startsWith("data:image")) return img;
    if (/^https?:\/\//i.test(img)) return img;
    return `data:image/${mime};base64,${img}`;
  }, []);

  const styles = {
    counterRow: {
      position: "absolute",
      bottom: "23px",
      left: isMobile ? "59%" : "54%",
      transform: "translateX(-50%)",
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)",
      gap: "15px",
      padding: "10px 15px",
      borderRadius: "15px",
      zIndex: 5,
      width: "100%",
      maxWidth: "1000px",
      textAlign: "center",
    },
    counterBox: { color: "#fff" },
    counterNumber: { fontSize: "40px", fontWeight: "bold", margin: "0" },
    counterText: { fontSize: "20px", margin: "5px 0 0" },
    icon: {
      fontSize: "35px",
      marginBottom: "10px",
      color: "white",
    },
  };

  const responsiveStyle = `
  @media (max-width: 768px) {
    .counter-number { font-size: 24px !important; }
    .counter-text { font-size: 14px !important; }
    .counter-icon { font-size: 24px !important; }
    .counter-row { gap: 10px !important; }
  }

  @media (max-width: 480px) {
    .counter-row {
      grid-template-columns: repeat(5, 1fr) !important;
    }
    .counter-number { font-size: 15px !important; }
    .counter-text { font-size: 9px !important; }
    .counter-icon { font-size: 16px !important; }
    .counter-row { gap: 5px !important; padding: 4px !important; }
  }
`;

  // Resize listener
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch banner data - WITHOUT TOKEN (public access)
  useEffect(() => {
    const fetchBanner = async () => {
      // showLoader();
      setNoBanner(false);

      try {
        // Try to get token if exists, but don't require it
        const token = localStorage.getItem("authToken");

        const response = await axios.get(
          "https://backend.maamaas.com/food/api/bannner/getall",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            timeout: 10000, // 10 second timeout
          }
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          const processed = response.data.map((b) => ({
            ...b,
            companyLogo: b.companyLogo ? toDataUrl(b.companyLogo) : DEFAULT_LOGO,
            companyBanner: b.companyBanner ? toDataUrl(b.companyBanner) : DEFAULT_BANNER,
          }));
          setBanners(processed);
        } else {
          // No banners from API - use default banner
          setNoBanner(true);
          setBanners([{
            bannerId: "default",
            companyName: "Maamaas House",
            establishedYear: "1997",
            companyLogo: DEFAULT_LOGO,
            companyBanner: DEFAULT_BANNER
          }]);
        }
      } catch (error) {
        console.error("❌ Failed to fetch banners:", error);
        // Show default banner on error
        setNoBanner(true);
        setBanners([{
          bannerId: "default",
          companyName: "Maamaas House",
          establishedYear: "1997",
          companyLogo: DEFAULT_LOGO,
          companyBanner: DEFAULT_BANNER
        }]);
      } finally {
        // hideLoader();
      }
    };

    fetchBanner();
  }, [toDataUrl, showLoader, hideLoader]);

  const [counts, setCounts] = useState({
    vendors: 0,
    users: 0,
    products: 0,
    happyCustomers: 0,
    totalOrders: 0
  });

  // Fetch users count - public access (without token requirement)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        
        const response = await fetch("https://backend.maamaas.com/subscription/api/admin/getallusers", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setCounts(prev => ({
            ...prev,
            users: data.length || 0,
          }));
        } else {
          // If API fails, use demo counts
          setCounts(prev => ({
            ...prev,
            users: 1250,
          }));
        }
      } catch (err) {
        console.error("Users API Error:", err);
        // Use demo data on error
        setCounts(prev => ({
          ...prev,
          users: 1250,
        }));
      }
    };

    fetchUsers();
  }, []);

  // Fetch orders and other data - public access
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const ordersResponse = await axios.get(
          "https://backend.maamaas.com/food/api/orders",
          { headers, timeout: 10000 }
        );

        const ordersData = ordersResponse.data;

        const totalOrders = Array.isArray(ordersData) ? ordersData.length : 0;

        const uniqueVendors = new Set(
          Array.isArray(ordersData)
            ? ordersData
                .filter(o => o.vendorId !== null && o.vendorId !== undefined)
                .map(o => o.vendorId)
            : []
        );

        const totalProducts = Array.isArray(ordersData)
          ? ordersData.reduce((total, order) => {
              if (Array.isArray(order.order)) {
                return (
                  total +
                  order.order.reduce(
                    (sum, item) => sum + (item.quantity || 0),
                    0
                  )
                );
              }
              return total;
            }, 0)
          : 0;

        setCounts(prev => ({
          ...prev,
          vendors: uniqueVendors.size > 0 ? uniqueVendors.size : 45,
          products: totalProducts > 0 ? totalProducts : 320,
          totalOrders: totalOrders > 0 ? totalOrders : 1850,
        }));
      } catch (err) {
        console.error("Orders API Error:", err);
        // Use demo data on error
        setCounts(prev => ({
          ...prev,
          vendors: 45,
          products: 320,
          totalOrders: 1850,
        }));
      }
    };

    fetchData();
  }, []);

  // Text animation
  useEffect(() => {
    const timer = setTimeout(() => setTextVisible(true), 500);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  // Auto-slide
  useEffect(() => {
    if (!banners.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
      setTextVisible(false);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners]);

  const currentBanner = banners[currentIndex] || {
    companyName: "Maamaas House",
    establishedYear: "1997",
    companyLogo: DEFAULT_LOGO,
    companyBanner: DEFAULT_BANNER
  };

  return (
    <div style={{ width: "100%", margin: 0, padding: 0, fontFamily: "'Poppins', sans-serif" }}>
      <div style={{ position: "relative", overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}>
        {/* Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.3))",
            zIndex: 2,
          }}
        ></div>

        {/* Slider */}
        <div
          style={{
            display: "flex",
            transition: "transform 0.8s ease-in-out",
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {banners.map((b, index) => (
            <img
              key={b.bannerId || index}
              src={b.companyBanner || DEFAULT_BANNER}
              alt={`banner-${index}`}
              onError={(e) => {
                e.target.src = DEFAULT_BANNER;
              }}
              style={{
                flexShrink: 0,
                width: "100%",
                height: windowWidth <= 768 ? "400px" : "700px",
                objectFit: "cover",
              }}
            />
          ))}
        </div>

        {/* Logo */}
        {currentBanner?.companyLogo && (
          <img
            src={currentBanner.companyLogo}
            alt="logo"
            onError={(e) => {
              e.target.src = DEFAULT_LOGO;
            }}
            style={{
              position: "absolute",
              top: windowWidth <= 768 ? "75px" : "220px",
              left: windowWidth <= 768 ? "10px" : "40px",
              height: windowWidth <= 768 ? "60px" : "100px",
              width: "auto",
              zIndex: 4,
              objectFit: "contain",
              borderRadius: "50%",
            }}
          />
        )}

        {/* Text Overlay */}
        <div
          style={{
            position: "absolute",
            top: windowWidth <= 768 ? "36%" : "50%",
            left: windowWidth <= 768 ? "15px" : "40px",
            right: windowWidth <= 768 ? "15px" : "40px",
            zIndex: 5,
            display: "flex",
            flexDirection: windowWidth <= 768 ? "column" : "row",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: "280px",
              color: "#fff",
              textAlign: "left",
              maxWidth: "600px",
              transform: textVisible ? "translateY(0)" : "translateY(-15%)",
              opacity: textVisible ? 1 : 0,
              transition: "all 0.8s ease-in-out",
            }}
          >
            <h1
              style={{
                fontSize: windowWidth <= 768 ? "1.8rem" : "3rem",
                fontWeight: "bold",
                marginTop: windowWidth <= 768 ? "0" : "-20px",
                letterSpacing: "1px",
                textShadow: "2px 2px 6px rgba(0,0,0,0.8)",
                lineHeight: "1.2",
              }}
            >
              {currentBanner?.companyName || "Maamaas House"}
            </h1>
            <p
              style={{
                fontSize: windowWidth <= 768 ? "1rem" : "1.5rem",
                marginTop: "5px",
                fontWeight: 300,
                textShadow: "1px 1px 4px rgba(0,0,0,0.6)",
              }}
            >
              Since {currentBanner?.establishedYear || "1997"}
            </p>
          </div>
        </div>

        <div>
          <style>{responsiveStyle}</style>
          <div style={styles.counterRow} className="counter-row">
            {/* Users Counter */}
            <div style={styles.counterBox} className="counter-box">
              <FaUserFriends className="counter-icon" style={styles.icon} />
              <h2 style={styles.counterNumber} className="counter-number">
                {counts.users > 0 ? `${counts.users}+` : "1,250+"}
              </h2>
              <p style={styles.counterText} className="counter-text">Users</p>
            </div>
            
            {/* Partners/Vendors Counter */}
            <div style={styles.counterBox} className="counter-box">
              <FaStore className="counter-icon" style={styles.icon} />
              <h2 style={styles.counterNumber} className="counter-number">
                {counts.vendors > 0 ? `${counts.vendors}+` : "45+"}
              </h2>
              <p style={styles.counterText} className="counter-text">Partners</p>
            </div>

            {/* Products Counter */}
            <div style={styles.counterBox} className="counter-box">
              <FaBoxOpen className="counter-icon" style={styles.icon} />
              <h2 style={styles.counterNumber} className="counter-number">
                {counts.products > 0 ? `${counts.products}+` : "320+"}
              </h2>
              <p style={styles.counterText} className="counter-text">Products</p>
            </div>

            {/* Total Orders Counter */}
            <div style={styles.counterBox} className="counter-box">
              <FaShoppingCart className="counter-icon" style={styles.icon} />
              <h2 style={styles.counterNumber} className="counter-number">
                {counts.totalOrders > 0 ? `${counts.totalOrders}+` : "1,850+"}
              </h2>
              <p style={styles.counterText} className="counter-text">Total Orders</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cloudbannerall;
